import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { theme } from "../theme";

// ── Spotlight Tour (uvítací průvodce po prvním přihlášení) ──────────────────────
// `route` říká, na které stránce cílový prvek žije — tour tam sám přepne.
export const TOUR_STEPS = [
  { id: "tour-attention", route: "/", needsProducts: true, title: "Vyžaduje pozornost", text: "Tady vidíte, co má cenu řešit hned — kde snížit cenu, abyste předběhli konkurenci, kde jste klesli a kde držíte první místo." },
  { id: "tour-health",    route: "/", needsProducts: true, title: "Zdraví portfolia", text: "Rychlý přehled, jak si celkově vedete. Průměrná pozice a jestli se lepší, nebo zhoršuje oproti minulé kontrole." },
  { id: "tour-chart",     route: "/", needsProducts: true, title: "Vývoj v čase", text: "Graf ukazuje, jak se vaše průměrná pozice vyvíjí za posledních 30 dní. Naplní se, jak sbíráme data." },
  { id: "tour-add",       route: "/produkty", title: "Přidání produktu", text: "Sem vložíte URL produktu z Heureky a Rankio ho začne automaticky sledovat. Můžete přidat i více URL najednou." },
  { id: "tour-settings",  route: "/produkty", title: "Nastavení", text: "Tady měníte plán, frekvenci kontrol a e-mail pro notifikace. Najdete tu i zrušení účtu." },
];

export default function SpotlightTour({ onFinish, hasProducts = true }) {
  // Prázdný účet nemá co ukazovat na dashboardu — zbydou kroky o přidání
  // produktu a nastavení, což je pro začátek přesně to podstatné.
  const steps = useMemo(
    () => TOUR_STEPS.filter(s => hasProducts || !s.needsProducts),
    [hasProducts]
  );
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const current = steps[step];

  // přepni na stránku, kde cílový prvek žije
  useEffect(() => {
    if (current.route && location.pathname !== current.route) navigate(current.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, current.route]);

  useEffect(() => {
    // najdi cílový prvek a spočítej jeho pozici
    const measure = () => {
      const el = document.getElementById(current.id);
      if (!el) { setRect(null); return; }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // malé zpoždění, ať scroll doběhne, pak změř
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }, 320);
    };
    measure();
    // po přepnutí routy se prvek vykreslí až v dalším cyklu — zkus to ještě jednou
    const retry = setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(retry); window.removeEventListener("resize", measure); };
  }, [step, current.id, location.pathname]);

  const finish = () => { onFinish(); };
  const next = () => { if (step < steps.length - 1) setStep(step + 1); else finish(); };
  const prev = () => { if (step > 0) setStep(step - 1); };

  const pad = 8;
  const spot = rect ? {
    top: rect.top - pad, left: rect.left - pad,
    width: rect.width + pad * 2, height: rect.height + pad * 2,
  } : null;

  // pozice bubliny: pod prvkem, nebo nad ním když je moc dole
  const bubbleTop = spot ? (spot.top + spot.height + 14) : 120;
  const placeBelow = spot ? (bubbleTop + 190 < window.innerHeight) : true;
  const bubbleStyle = spot ? (
    placeBelow
      ? { top: spot.top + spot.height + 14, left: Math.max(16, Math.min(spot.left, window.innerWidth - 380)) }
      : { top: Math.max(16, spot.top - 200), left: Math.max(16, Math.min(spot.left, window.innerWidth - 380)) }
  ) : { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500 }}>
      {/* tmavý overlay se "světlem" (dírou) na cílovém prvku */}
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <mask id="spot-mask">
            <rect width="100%" height="100%" fill="white" />
            {spot && <rect x={spot.left} y={spot.top} width={spot.width} height={spot.height} rx="16" fill="black" />}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(15,23,42,0.72)" mask="url(#spot-mask)" />
        {spot && <rect x={spot.left} y={spot.top} width={spot.width} height={spot.height} rx="16" fill="none" stroke={theme.blue} strokeWidth="2.5" />}
      </svg>

      {/* bublina s popisem */}
      <div style={{
        position: "absolute", ...bubbleStyle, width: 340, maxWidth: "calc(100vw - 32px)",
        background: theme.bg, borderRadius: theme.rCard, padding: 22,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)", fontFamily: theme.font,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.blue, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Krok <span style={{ fontVariantNumeric: theme.tnum }}>{step + 1}</span> z <span style={{ fontVariantNumeric: theme.tnum }}>{steps.length}</span>
          </span>
          <button onClick={finish} style={{ background: "none", border: "none", color: theme.mut, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Přeskočit</button>
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: theme.ink, marginBottom: 8, letterSpacing: "-0.3px" }}>{current.title}</h3>
        <p style={{ fontSize: 14, color: theme.inkSoft, lineHeight: 1.6, marginBottom: 20 }}>{current.text}</p>
        {/* tečky + tlačítka */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {steps.map((_, i) => (
              <span key={i} style={{ width: i === step ? 20 : 7, height: 7, borderRadius: 100, background: i === step ? theme.blue : theme.line, transition: "all .2s" }} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button onClick={prev} style={{ background: theme.bg, border: `1.5px solid ${theme.line}`, borderRadius: theme.rBtn, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: theme.inkSoft, cursor: "pointer", fontFamily: "inherit" }}>Zpět</button>
            )}
            <button onClick={next} style={{ background: theme.blue, border: "none", borderRadius: theme.rBtn, padding: "8px 18px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
              {step < steps.length - 1 ? "Další →" : "Hotovo"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
